import type { ReactElement } from 'react';
import { useMemo } from 'react';
import type { Editor, Range } from '@tiptap/react';
import type { EmojiItem } from '@tiptap/extension-emoji';

// --- Tiptap UI ---
import type {
  SuggestionItem,
  SuggestionMenuProps,
  SuggestionMenuRenderProps,
} from 'containers/Tiptap/components/tiptap-ui-utils/suggestion-menu';
import { SuggestionMenu } from 'containers/Tiptap/components/tiptap-ui-utils/suggestion-menu';
import { EmojiMenuItem, getFilteredEmojis } from 'containers/Tiptap/components/tiptap-ui/emoji-menu';
import { Card, CardBody } from 'containers/Tiptap/components/tiptap-ui-primitive/card';
import { ButtonGroup } from 'containers/Tiptap/components/tiptap-ui-primitive/button';

export type EmojiDropdownMenuProps = Omit<SuggestionMenuProps, 'items' | 'children'>;

export const EmojiDropdownMenu = (props: EmojiDropdownMenuProps): ReactElement => (
  <SuggestionMenu
    char=":"
    pluginKey="emojiDropdownMenu"
    decorationClass="tiptap-emoji-decoration"
    selector="tiptap-emoji-dropdown-menu"
    items={getSuggestionItems}
    {...props}
  >
    {(renderProps): ReactElement => <EmojiList {...renderProps} />}
  </SuggestionMenu>
);

const getSuggestionItems = async (props: { query: string; editor: Editor }): Promise<SuggestionItem[]> => {
  const emojis: EmojiItem[] = props.editor.extensionStorage.emoji.emojis || [];
  const filteredEmojis = getFilteredEmojis({ query: props.query, emojis });

  return filteredEmojis.map(
    (emoji): SuggestionItem => ({
      title: emoji.name,
      subtext: emoji.shortcodes.join(', '),
      context: emoji,
      onSelect: (selectProps: { editor: Editor; range: Range; context?: EmojiItem }): void => {
        if (!selectProps.editor || !selectProps.range || !selectProps.context) return;
        selectProps.editor.chain().focus().setEmoji(selectProps.context.name).run();
      },
    })
  );
};

const EmojiList = ({ items, selectedIndex, onSelect }: SuggestionMenuRenderProps<EmojiItem>): ReactElement | null => {
  const renderedItems = useMemo((): ReactElement[] => {
    const rendered: ReactElement[] = [];

    items.forEach((item, index) => {
      if (!item.context) return;

      rendered.push(
        <EmojiMenuItem
          key={item.title}
          emoji={item.context}
          isSelected={index === selectedIndex}
          onSelect={(): void => onSelect(item)}
          selector="[data-selector='tiptap-emoji-dropdown-menu']"
        />
      );
    });

    return rendered;
  }, [items, selectedIndex, onSelect]);

  if (!renderedItems.length) {
    return null;
  }

  return (
    <Card
      style={{
        maxHeight: 'var(--suggestion-menu-max-height)',
      }}
    >
      <CardBody>
        <ButtonGroup>{renderedItems}</ButtonGroup>
      </CardBody>
    </Card>
  );
};
